import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { Button, TextInput } from 'react-native-paper';
import { useTranslation } from 'react-i18next';

import { ResumeLinkItem } from '../../../types';
import { Palette, spacing, useThemePalette } from '../../../theme';
import RepeatableItemCard from '../RepeatableItemCard';

interface Props {
  value: ResumeLinkItem[];
  onChange: (next: ResumeLinkItem[]) => void;
}

const LinksForm: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);

  const update = (idx: number, patch: Partial<ResumeLinkItem>) =>
    onChange(value.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const remove = (idx: number) => onChange(value.filter((_, i) => i !== idx));

  return (
    <View style={styles.container}>
      {value.map((item, idx) => (
        <RepeatableItemCard
          key={idx}
          title={item.label || item.url || `${t('resume.sections.links')} ${idx + 1}`}
          onRemove={() => remove(idx)}
        >
          <TextInput
            mode="outlined"
            label={t('resume.fields.linkLabel')}
            value={item.label}
            onChangeText={(v) => update(idx, { label: v })}
            dense
          />
          <TextInput
            mode="outlined"
            label={t('resume.fields.linkUrl')}
            value={item.url}
            onChangeText={(v) => update(idx, { url: v })}
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType="url"
            dense
          />
        </RepeatableItemCard>
      ))}
      <Button
        mode="outlined"
        icon="plus"
        onPress={() => onChange([...value, { label: '', url: '' }])}
        textColor={palette.textPrimary}
        style={styles.addBtn}
      >
        {t('resume.buttons.addLink')}
      </Button>
    </View>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    container: { gap: spacing.sm },
    addBtn: { borderRadius: 999, borderColor: p.border, marginTop: spacing.xs },
  });

export default LinksForm;
